import React, { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'
import { Controlled as CodeMirror } from 'react-codemirror2'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import _ from 'lodash'
import momentTz from 'moment-timezone'

import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'

import { fetchAllTemporaryData, fetchConfigFiles, importStaticTemporaryData, saveConfigFile } from './importer-api'
import { STANDARD_CSV } from './utils/data'
import { GlobalNotification } from './global-components/GlobalNotification'
import { transformConfigIntoStringInJSONFormat } from './utils/utils'
import { ImportDataErrors } from './global-components/ImportDataErrors'

interface ImportDataProps {
    setIsImportDataSideBarOpen: (value: boolean) => void
    setTemporaryData: (value: TemporaryData[]) => void
    setIsFetching: (value: boolean) => void
}

const ImportData = ({ setIsImportDataSideBarOpen, setTemporaryData, setIsFetching }: ImportDataProps) => {
    const [configErrors, setConfigErrors] = useState<any[]>([])
    // CONFIG
    const [configFiles, setConfigFiles] = useState<Record<string, ConfigFile>>()
    const [configFileOptions, setConfigFileOptions] = useState<SelectOptionsTypes[]>([
        { label: i18n('New Configuration'), value: 'NEW_CONFIG' }
    ])
    const [selectedConfig, setSelectedConfig] = useState<SelectOptionsTypes>()
    const [configName, setConfigName] = useState('')
    const [jsonConfig, setJsonConfig] = useState<Config | string>('')
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false)
    const [hasNewConfigTheSameName, setHasNewConfigTheSameName] = useState(false)
    const isStandardConfigSelected = selectedConfig?.value === 'standardConfiguration'
    // CSV
    const [csvFile, setCsvFile] = useState(null)
    const fileCsvInputRef = useRef<HTMLInputElement>(null)
    const downloadButtonRef = useRef<HTMLAnchorElement | null>(null)
    const csvContent = 'data:text/csv;charset=utf-8,' + STANDARD_CSV.map(row => row.join(';')).join('\n')
    const downloadCSVData = encodeURI(csvContent)

    useEffect(() => {
        fetchConfigFiles().then(setConfigFiles)
    }, [])

    useEffect(() => {
        if (configFiles && !configName) {
            const options = Object.keys(configFiles).map(name => {
                const isStandardConfiguration = name === 'standardConfiguration'
                if (isStandardConfiguration) {
                    setSelectedConfig({
                        label: i18n('Standard Configuration'),
                        value: name
                    })
                    setConfigName(name)
                    setJsonConfig(configFiles[name].config)
                }
                return {
                    label: isStandardConfiguration ? i18n('Standard Configuration') : name,
                    value: name
                }
            })
            const standardOption = options.filter(option => option.value === 'standardConfiguration')
            setConfigFileOptions(prevState => [
                ...prevState,
                ...standardOption,
                ...options.filter(option => option.value !== 'standardConfiguration')
            ])
        }
    }, [configFiles])

    useEffect(() => {
        if (configErrors.length > 0) {
            document.getElementById('errors-info-container').focus()
        }
    }, [configErrors])

    const transformErrorMapIntoArray = (map: Record<string, Record<string, Record<string, string>>>) => {
        const transformedData = []

        for (const key in map) {
            const transformedMapping = transformData(map[key])
            const obj = {}
            obj[key] = transformedMapping
            transformedData.push(obj)
        }

        return transformedData
    }

    const transformData = (map: Record<string, Record<string, string>>) => {
        const resultData = []

        for (const entityKey in map) {
            const entity = []
            for (const fieldName in map[entityKey]) {
                entity.push({ fieldName, message: map[entityKey][fieldName] })
            }
            const newObj = {}
            newObj[entityKey] = entity
            resultData.push(newObj)
        }

        return resultData
    }

    const validateConfigBeforeImport = (errors: Record<string, Record<string, Record<string, string>>>) => {
        const transformedData = transformErrorMapIntoArray(errors)
        setConfigErrors(transformedData)
    }

    const handleCsvFileChange = e => {
        const newCsvFile = e.target.files[0]
        setCsvFile(newCsvFile)
    }

    const handleCsvSubmit = () => {
        const timezone = momentTz.tz.guess()
        if (csvFile && configName && timezone) {
            setIsFetching(true)
            importStaticTemporaryData(configName, csvFile, timezone)
                .then(errors => {
                    if (errors?.message || errors?.body?.message) return
                    if (!_.isEmpty(errors)) {
                        validateConfigBeforeImport(errors)
                        return
                    }
                    fetchAllTemporaryData()
                        .then(data => {
                            setTemporaryData(data)
                            globalNotification.success(
                                <GlobalNotification title="Data has been imported successfully" />,
                                7
                            )
                        })
                        .finally(() => setIsFetching(false))
                    setIsImportDataSideBarOpen(false)
                })
                .finally(() => setIsFetching(false))
        }
    }

    const handleCsvConfigButton = () => {
        fileCsvInputRef.current?.click()
    }

    const handleSelectConfigChange = (option: SelectOptionsTypes) => {
        setSelectedConfig(option)

        if (option.value === 'NEW_CONFIG') {
            setJsonConfig(configFiles['standardConfiguration'].config)
            setConfigName('')
            return
        }
        setIsSaveButtonDisabled(true)
        setJsonConfig(configFiles[option.value].config)
        setConfigName(option.value)
    }

    const handleConfigSubmit = () => {
        if (configName && jsonConfig) {
            saveConfigFile(configName, transformConfigIntoStringInJSONFormat(jsonConfig)).then(res => {
                if (res.status && res.status !== 200) return
                setConfigFiles(prevState => ({ ...prevState, [configName]: res }))
                setConfigFileOptions(prevState => [...prevState, { label: configName, value: configName }])
                setSelectedConfig({ value: configName, label: configName })
                setIsSaveButtonDisabled(true)

                globalNotification.success(
                    <GlobalNotification
                        title={`Import Configuration has been ${
                            !configFiles[configName] ? 'saved' : 'updated'
                        } successfully`}
                    />,
                    10
                )
            })
        }
    }

    const handleResetConfig = () => {
        if (selectedConfig.value === 'NEW_CONFIG') {
            setConfigName('')
            setJsonConfig('')
            return
        }
        setJsonConfig(configFiles[configName].config)
        setIsSaveButtonDisabled(true)
    }

    return (
        <ImporterDataContainer>
            <ImporterHeader>
                <h2 className="import-data-title"> {i18n('Import Data')}</h2>
            </ImporterHeader>

            <ImporterMain isStandardJSONConfig={isStandardConfigSelected}>
                <div className="config-file">
                    <div className="title-and-icon">
                        <span className="config-file-span">{i18n('Import Configuration')}</span>
                    </div>
                    <Select
                        options={configFileOptions}
                        value={selectedConfig ?? ''}
                        onChange={(value: string, option: SelectOptionsTypes) => handleSelectConfigChange(option)}
                        placeholder={'Select configuration...'}
                        clearable={false}
                    />
                    <div className="code-mirror-container">
                        <CodeMirror
                            value={transformConfigIntoStringInJSONFormat(jsonConfig)}
                            onBeforeChange={(editor, data, value) => {
                                setJsonConfig(JSON.parse(value))
                                setIsSaveButtonDisabled(false)
                            }}
                            options={{
                                mode: 'json',
                                matchBrackets: true,
                                lineNumbers: true,
                                foldGutter: true,
                                change: true,
                                readOnly: isStandardConfigSelected,
                                autofocus: isStandardConfigSelected,
                                disableInput: isStandardConfigSelected
                            }}
                        />
                    </div>
                    <div className="message-info">
                        <i className="far fa-exclamation-circle" />
                        <span>
                            {isStandardConfigSelected
                                ? i18n(
                                      'The values ​​defined in Standard Configuration correspond to the respective column and enum values ​​in the Template CSV. If you want to change these values, select another configuration option.'
                                  )
                                : i18n(
                                      'The values ​​defined on the left must not be changed. The values ​​on the right must be the same as the respective column and enum values ​​in the CSV file.'
                                  )}
                        </span>
                    </div>
                    {selectedConfig?.value === 'NEW_CONFIG' && (
                        <div className="new-name-container">
                            <span>{i18n('Import Configuration Name')}</span>
                            <input
                                className="new-name-config-input"
                                style={{ borderColor: hasNewConfigTheSameName ? 'var(--red-500)' : 'var(--gray-400)' }}
                                type="text"
                                value={configName}
                                onChange={e => {
                                    setConfigName(e.target.value)
                                    setHasNewConfigTheSameName(!!configFiles[e.target.value])
                                    setIsSaveButtonDisabled(false)
                                }}
                                placeholder={i18n('Define a name for the new config...')}
                                autoFocus
                            />
                            {hasNewConfigTheSameName && (
                                <div className="same-name-info">
                                    <i className="fas fa-exclamation-triangle" />
                                    <span>
                                        {i18n(
                                            'A file with this name already exists. Enter a different name or click the Save button to overwrite it.'
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    {!isStandardConfigSelected && (
                        <div className="acts">
                            <button
                                className={`save-act-btn ${!isSaveButtonDisabled ? 'primary' : 'secondary'}-btn`}
                                onClick={handleConfigSubmit}
                                disabled={!configName || isSaveButtonDisabled}
                            >
                                {i18n('Save')}
                            </button>
                            <button
                                onClick={handleResetConfig}
                                className="reset-act-btn secondary-btn"
                                disabled={isSaveButtonDisabled}
                            >
                                {i18n('Reset')}
                            </button>
                        </div>
                    )}
                </div>

                <div className="csv-data-file">
                    <span className="csv-file-span">{i18n('Static Data Input File')}</span>
                    <div className="csv-file acts">
                        <button
                            className={`import-csv-btn ${
                                isStandardConfigSelected ? 'primary' : isSaveButtonDisabled ? 'primary' : 'secondary'
                            }-btn`}
                            onClick={handleCsvConfigButton}
                        >
                            <i className="fas fa-upload" />
                            {i18n('Upload CSV')}
                        </button>
                        <input
                            id="upload-csv"
                            type="file"
                            accept=".csv"
                            onChange={e => handleCsvFileChange(e)}
                            ref={fileCsvInputRef}
                        />
                        <a
                            href={downloadCSVData}
                            download="static-data-input-template.csv"
                            className="link-hidden"
                            ref={downloadButtonRef}
                        />
                        <button
                            className="download-csv-btn secondary-btn"
                            onClick={() => downloadButtonRef.current?.click()}
                        >
                            <i className="fas fa-download" />
                            {i18n('Download CSV template')}
                        </button>
                    </div>
                    {csvFile?.name && (
                        <input className="csv-name-file-input" type="text" disabled value={csvFile.name} />
                    )}

                    <div className="message-info">
                        <i className="far fa-exclamation-circle" />
                        <span>
                            {i18n(
                                'The CSV must use a semicolon (;) as a delimiter between values.'
                            )}
                        </span>
                    </div>
                </div>

                {configErrors?.length > 0 && (
                    <div tabIndex={-1} id="errors-info-container" className="errors-info-container">
                        <div className="error-content">
                            <ImportDataErrors data={configErrors} />
                        </div>
                        <div className="info">
                            <i className="fas fa-exclamation-triangle" />
                            <span>
                                <b>{i18n('Reviews required: ')}</b>
                                {i18n(
                                    'You must fix errors identified in the Import Configuration or CSV file, then confirm the data import.'
                                )}
                            </span>
                        </div>
                    </div>
                )}
            </ImporterMain>

            <ImporterFooter>
                <button
                    className={`submit-btn ${csvFile && configName ? 'primary' : 'secondary'}-btn`}
                    onClick={handleCsvSubmit}
                    disabled={!csvFile || (selectedConfig?.value === 'NEW_CONFIG' && !configName)}
                >
                    <i className="fa fal fa-file-download" />
                    {i18n('Confirm import data')}
                </button>
                <button className="cancel-btn secondary-btn" onClick={() => setIsImportDataSideBarOpen(false)}>
                    {i18n('Cancel')}
                </button>
            </ImporterFooter>
        </ImporterDataContainer>
    )
}

export default ImportData

const ImporterDataContainer = styled.aside`
    position: absolute;
    right: 0;
    top: 0;

    display: flex;
    flex-direction: column;
    justify-content: space-between;

    width: 400px;
    height: calc(100vh - 83px);

    box-shadow: 0 2px 14px 0 rgba(96, 105, 128, 0.4);
    overflow: hidden;
    transition: 0.5s ease-in-out;
    background-color: var(--gray-100);
`

const ImporterHeader = styled.header`
    padding: 16px;

    border-bottom: 1px solid var(--gray-400);
    background-color: var(--gray-0);

    .import-data-title {
        font-family: SourceSansPro-Bold, Arial, 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 16px;
        line-height: 16px;
        color: var(--gray-800);
        margin: 0;
    }
`

const ImporterMain = styled.main<{ isStandardJSONConfig: boolean }>`
    padding: 16px;
    display: flex;
    flex-direction: column;
    height: 100%;

    overflow-y: auto;

    // Changing scrollbar css
    /* width */
    ::-webkit-scrollbar {
        width: 8px;
    }

    /* outside */
    ::-webkit-scrollbar-track {
        background: var(--gray-100);
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
        border-radius: 8px;
        background: var(--gray-500);
    }

    .message-info {
        display: flex;
        gap: 4px;

        i {
            margin-top: 4px;
            color: var(--blue-500);
        }
    }

    .config-file {
        display: flex;
        flex-direction: column;
        flex: 1;

        .title-and-icon {
            display: flex;
            align-items: center;
            gap: 4px;

            .config-file-span {
                font-size: 13px;
                font-weight: bold;
                line-height: 18px;
                margin-bottom: 4px;
            }

            .fa-exclamation-circle {
                margin-top: -3px;

                color: var(--blue-500);
            }
        }

        .new-name-container {
            display: flex;
            flex-direction: column;

            span {
                margin-top: 16px;
                font-size: 13px;
                font-weight: bold;
                line-height: 18px;
                margin-bottom: 4px;
            }

            .new-name-config-input {
                border-radius: 3px;
            }

            .same-name-info {
                margin-top: 4px;
                display: flex;
                gap: 8px;
                color: var(--red-500);

                i {
                    margin-top: 3px;
                }

                span {
                    margin-top: 0;
                    font-size: 13px;
                    font-weight: 400;
                    line-height: 18px;
                }
            }
        }

        .code-mirror-container {
            margin: 16px 0;

            .react-codemirror2 {
                border-radius: 3px;
                border: 1px solid var(--gray-400);

                .cm-s-default {
                    height: 270px;
                }
            }

            .CodeMirror-vscrollbar,
            .CodeMirror-hscrollbar {
                z-index: 1;
                // Changing scrollbar css
                /* width */
                &::-webkit-scrollbar {
                    width: 8px;
                }

                /* outside */
                &::-webkit-scrollbar-track {
                    background-color: ${({ isStandardJSONConfig }) =>
                        isStandardJSONConfig ? 'var(--gray-200)' : 'var(--gray-100)'};
                }

                /* Handle */
                &::-webkit-scrollbar-thumb {
                    border-radius: 8px;
                    background-color: var(--gray-500);
                }
            }
            .CodeMirror-hscrollbar {
                > div {
                    background-color: ${({ isStandardJSONConfig }) =>
                        isStandardJSONConfig ? 'var(--gray-200)' : 'var(--gray-0)'};
                }

                &::-webkit-scrollbar {
                    height: 8px;
                }
            }

            .CodeMirror-scroll {
                background-color: ${({ isStandardJSONConfig }) =>
                    isStandardJSONConfig ? 'var(--gray-200)' : 'var(--gray-0)'};

                pointer-events: ${({ isStandardJSONConfig }) => (isStandardJSONConfig ? 'none' : 'initial')};

                .CodeMirror-gutters {
                    background-color: ${({ isStandardJSONConfig }) =>
                        isStandardJSONConfig ? 'var(--gray-200)' : 'var(--gray-0)'};
                }

                .CodeMirror-code {
                    pre.CodeMirror-line {
                        color: ${({ isStandardJSONConfig }) =>
                            isStandardJSONConfig ? 'var(--gray-500)' : 'var(--gray-800)'};

                        span.CodeMirror-matchingbracket {
                            color: ${({ isStandardJSONConfig }) =>
                                isStandardJSONConfig ? 'var(--gray-500)' : 'var(--gray-800)'};
                            background-color: ${({ isStandardJSONConfig }) =>
                                isStandardJSONConfig ? 'var(--gray-200)' : 'var(--gray-0)'};
                        }
                    }
                }
            }
        }

        .form-group {
            margin-bottom: 0;

            .Select-control {
                margin-top: 2px;
                margin-bottom: 0 !important;
                border-radius: 3px !important;
                border: 1px solid var(--gray-400);
                font-size: 14px;

                .Select-placeholder {
                    color: var(--gray-600) !important;
                }
            }
        }

        .console-content {
            margin: 20px 0;
            border: 1px solid var(--gray-400);
            border-radius: 3px;

            .CodeMirror-vscrollbar {
                // Changing scrollbar css
                /* width */
                ::-webkit-scrollbar {
                    width: 8px !important;
                }

                /* outside */
                ::-webkit-scrollbar-track {
                    background: var(--gray-100) !important;
                }

                /* Handle */
                ::-webkit-scrollbar-thumb {
                    border-radius: 8px !important;
                    background: var(--gray-500) !important;
                }
            }
        }
    }

    .csv-data-file {
        display: flex;
        flex-direction: column;

        margin-top: 16px;
        padding: 16px 0;
        border-top: 1px solid var(--gray-400);

        .csv-file-span {
            font-size: 16px;
            font-weight: bold;
            line-height: 20px;
            margin-bottom: 4px;
        }

        .csv-file {
            align-items: center;
            margin-top: 12px;
            margin-bottom: 16px;

            span {
                max-width: 116px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .link-hidden {
                display: none;
            }
        }

        input[type='file'] {
            display: none;
        }

        .csv-name-file-input {
            margin-bottom: 16px;
            color: var(--gray-800);
        }
    }

    .errors-info-container {
        display: flex;
        flex-direction: column;

        padding: 16px 0;
        border-top: 1px solid var(--gray-400);

        .error-content {
            display: flex;
            flex-direction: column;

            padding: 8px;
            max-height: 150px;

            font-size: 12px;

            border-radius: 3px;
            background-color: var(--yellow-200);

            overflow-y: auto;

            // Changing scrollbar css
            /* width */
            ::-webkit-scrollbar {
                width: 8px;
            }

            /* outside */
            ::-webkit-scrollbar-track {
                background: var(--yellow-200);
            }

            /* Handle */
            ::-webkit-scrollbar-thumb {
                border-radius: 8px;
                background: var(--gray-500);
            }

            .error {
                display: flex;
                align-items: start;
                gap: 4px;
            }

            .error + .error {
                margin-top: 4px;
            }
        }

        .info {
            margin-top: 4px;
            display: flex;
            gap: 8px;
            color: var(--red-500);

            i {
                margin-top: 3px;
            }

            span {
                margin-top: 0;
                font-size: 13px;
                font-weight: 400;
                line-height: 18px;
            }
        }
    }

    .acts {
        display: flex;
        align-items: center;
        gap: 8px;

        margin-top: 16px;
    }

    button {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4px;

        width: fit-content;
        min-width: 75px;
        height: 30px;
        font-size: 12px;
        font-weight: 700;
        border-radius: 4px;
        border: none;

        &.primary-btn {
            color: white;
            background-color: var(--blue-500);

            &:disabled {
                opacity: 0.5;
                background-color: var(--blue-500);
            }
        }

        &.secondary-btn {
            border: 1px solid var(--gray-400);
            background-color: transparent;

            &:disabled {
                opacity: 0.5;
                background-color: var(--gray-200);
            }
        }

        &:hover:not(:disabled) {
            opacity: 0.8;
        }
    }
`

const ImporterFooter = styled.footer`
    padding: 16px;
    display: flex;
    gap: 8px;

    border-top: 1px solid var(--gray-400);
    background-color: var(--gray-0);

    button {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 4px;

        width: fit-content;
        min-width: 75px;
        height: 30px;
        font-size: 12px;
        font-weight: 700;
        border-radius: 4px;
        border: none;

        &.primary-btn {
            color: white;
            background-color: var(--blue-500);

            &:disabled {
                opacity: 0.5;
                background-color: var(--blue-500);
            }
        }

        &.secondary-btn {
            border: 1px solid var(--gray-400);
            background-color: transparent;

            &:disabled {
                opacity: 0.5;
                background-color: var(--gray-200);
            }
        }

        &:hover:not(:disabled) {
            opacity: 0.8;
        }
    }
`
